/**
 * --------------------------------------------------------------------------
 * CoreUI PRO data-grid ui/editor.ts
 * License (https://coreui.io/pro/license/)
 * --------------------------------------------------------------------------
 */

import type {
  DataGridEditableOptions, DataGridEditor, DataGridEditorContext
} from '../types'

const CLASS_NAME_EDITOR = 'data-grid-cell-editor'

const createTextEditor = (context: DataGridEditorContext): DataGridEditor => {
  const input = document.createElement('input')
  input.type = 'text'
  input.classList.add('form-control', 'form-control-sm', CLASS_NAME_EDITOR)
  input.value = String(context.value ?? '')

  return {
    element: input,
    focus() {
      input.focus()
      input.select()
    },
    getValue: () => input.value
  }
}

const createNumberEditor = (context: DataGridEditorContext, options: DataGridEditableOptions): DataGridEditor => {
  const input = document.createElement('input')
  input.type = 'number'
  input.classList.add('form-control', 'form-control-sm', CLASS_NAME_EDITOR)
  if (options.min !== undefined) {
    input.min = String(options.min)
  }

  if (options.max !== undefined) {
    input.max = String(options.max)
  }

  if (options.step !== undefined) {
    input.step = String(options.step)
  }

  input.value = context.value === undefined || context.value === null ? '' : String(context.value)

  return {
    element: input,
    focus() {
      input.focus()
      input.select()
    },
    getValue: () => (input.value === '' ? undefined : Number(input.value))
  }
}

const createSelectEditor = (context: DataGridEditorContext, options: DataGridEditableOptions): DataGridEditor => {
  const select = document.createElement('select')
  select.classList.add('form-select', 'form-select-sm', CLASS_NAME_EDITOR)

  // Option values can be arbitrary (not just strings) - keep the originals
  // and resolve the committed value by index.
  const values: unknown[] = []
  for (const option of options.options ?? []) {
    const optionElement = document.createElement('option')
    const value = typeof option === 'object' && option !== null ? option.value : option
    optionElement.textContent = typeof option === 'object' && option !== null ? option.label : String(option)
    optionElement.value = String(values.length)
    if (value === context.value) {
      optionElement.selected = true
    }

    values.push(value)
    select.append(optionElement)
  }

  return {
    element: select,
    focus: () => select.focus(),
    getValue: () => values[Number(select.value)]
  }
}

export const createBuiltInEditor = (
  options: DataGridEditableOptions,
  context: DataGridEditorContext
): DataGridEditor => {
  if (options.type === 'number') {
    return createNumberEditor(context, options)
  }

  if (options.type === 'select') {
    return createSelectEditor(context, options)
  }

  return createTextEditor(context)
}
